import { useParams, Link, useNavigate } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArticleContent } from '@/components/ui/ArticleContent'
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import {
  useProgramme,
  usePublishedProgrammes
} from '@/lib/api/hooks/programme'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'
import MicrositeDetails from '@/components/pages/MicroSite/MicroSiteDetails'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'

export default function ProgramDetail() {
  const { programmeId } = useParams()
  const navigate = useNavigate()
  const urlQueryParams = new URLSearchParams(window.location.search);
  const micrositeTemplate = urlQueryParams.get('template');

  // Fetch programme data from API
  const { data: programmeData, isLoading } = useProgramme(programmeId)
  const { data: allProgrammesData } = usePublishedProgrammes({ page: 1, size: 100 }) // Fetch enough to enable navigation

  // Extract data from API
  const apiProgramme = programmeData?.data
  const allProgrammes = allProgrammesData?.data?.data || []

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  // Programme data from API
  const programme = apiProgramme ? {
    id: programmeId || '',
    title: apiProgramme.title,
    heroImage: apiProgramme.coverImageUrl,
    date: apiProgramme.datePublished ? formatDate(apiProgramme.datePublished) : formatDate(apiProgramme.createdAt),
    altText: apiProgramme.coverImageTitle || apiProgramme.title,
    content: apiProgramme.content || '',
    articleTag: apiProgramme.articleTag || [],
    isAddWebsite: apiProgramme.isAddWebsite || false,
    websiteType: apiProgramme.websiteType || '',
    websiteLink: apiProgramme.websiteLink || '',
    microSite: apiProgramme.microSite || null
  } : null

  // Find current programme index in the list for navigation
  const currentProgrammeIndex = allProgrammes.findIndex(p => p.programmeId === programmeId)
  const hasPrevious = currentProgrammeIndex > 0
  const hasNext = currentProgrammeIndex >= 0 && currentProgrammeIndex < allProgrammes.length - 1
  const previousProgramme = hasPrevious ? allProgrammes[currentProgrammeIndex - 1] : null
  const nextProgramme = hasNext ? allProgrammes[currentProgrammeIndex + 1] : null

  // Get other programmes for sidebar (excluding current programme)
  const otherProgrammes = allProgrammes
    .filter(p => p.programmeId !== programmeId)
    .slice(0, 4)
    .map((p) => ({
      id: p.programmeId,
      title: p.title,
      image: p.coverImageUrl
    }))

  const handleShare = (platform: string) => {
    if (!programme) return

    const url = window.location.href
    const title = programme.title

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')
        break
      case 'instagram':
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(url)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Programmes', href: '/programmes/programs' },
    { label: programme?.title || 'Loading...', href: '', active: true }
  ]

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner fullScreen size="lg" message="Loading programme..." />
  }

  // Show error state if programme not found
  if (!programme) {
    return (
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Programme not found</p>
          <Link to="/programmes/programs" className="text-wine-100 hover:underline mt-4 inline-block">
            Back to Programmes
          </Link>
        </div>
      </div>
    )
  }

  if(micrositeTemplate){
    return <MicrositeDetails />;
  };

  return (
    <motion.div
      className="max-w-[1440px] bg-white mx-auto px-4 sm:px-6 lg:px-8"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="mb-5" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Main Content Container */}
      <motion.div
        className="space-y-16"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportReveal}
      >
        {/* Hero Section */}
        <div className="space-y-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
            {programme.title}
          </h1>

          <div className="space-y-2">
            <img
              src={programme.heroImage}
              alt={programme.altText}
              className="w-full h-64 sm:h-80 md:h-96 lg:h-[502px] object-cover rounded"
            />

            {programme.date && (
              <p className="text-base font-medium font-satoshi text-gray-500">
                {programme.date}
              </p>
            )}

            {/* External Button */}
{
            programme.isAddWebsite ? (<button className='inline-flex items-center gap-2 px-6 py-3 bg-wine-100 text-white rounded-lg hover:bg-wine-200 transition-colors text-base font-medium'
              onClick={() => {
                if (programme.websiteType === "WEBSITE_LINK" && programme.websiteLink) {
                  window.open(programme.websiteLink, '_blank');
                } else if (programme.websiteType === "MICROSITE" && programme.microSite) {
                  const url = `?title=${encodeURIComponent(programme.title)}&template=${encodeURIComponent(programme.microSite.micrositeTemplateType)}`;
window.open(url, '_blank');
                }
              }}
            >
              Visit the website <ChevronRight className="w-5 h-5" />
            </button>) : null
}
          </div>
        </div>

        {/* Content and Sidebar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1">
            <div className="space-y-12">
              {/* Article Content */}
              <div className="space-y-10">
                {/* Content */}
                <div className="space-y-6">
                  <ArticleContent content={programme.content} className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed" />
                </div>



                {/* Tags */}
                {Array.isArray(programme.articleTag) && programme.articleTag.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {programme.articleTag.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-50 rounded-md text-base font-medium  hover:bg-gray-200 transition-colors cursor-pointer">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Sharing */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="text-lg font-normal font-pt-serif text-gray-600">
                  SHARE
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                    aria-label="Share on Instagram"
                  >
                    <Instagram className="w-full h-full" />
                  </button>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                {hasPrevious && previousProgramme ? (
                  <button
                    onClick={() => navigate(`/programmes/${previousProgramme.programmeId}`)}
                    className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-base font-medium font-satoshi text-gray-900">
                      Previous
                    </span>
                  </button>
                ) : (
                  <div className="w-full sm:w-auto"></div>
                )}

                {hasNext && nextProgramme ? (
                  <button
                    onClick={() => navigate(`/programmes/${nextProgramme.programmeId}`)}
                    className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors ml-auto"
                  >
                    <span className="text-base font-medium font-satoshi text-gray-900">
                      Next
                    </span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <div className="w-full sm:w-auto"></div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 order-2">
            <div className="space-y-10">
              {/* Other Programmes */}
              {otherProgrammes.length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    OTHER PROGRAMMES
                  </h3>

                  <div className="space-y-8">
                    {otherProgrammes.map((program) => (
                      <Link
                        key={program.id}
                        to={`/programmes/${program.id}`}
                        className="group block"
                      >
                        <article className="space-y-4">
                          <div className="w-full h-72">
                            <img
                              src={program.image}
                              alt={program.title}
                              className="w-full h-full object-cover rounded group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <h4 className="text-lg font-normal font-pt-serif text-gray-900 leading-relaxed group-hover:text-wine-100 transition-colors">
                            {program.title}
                          </h4>
                        </article>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Articles */}
              <RecentArticles />

              {/* Newsletter */}
              <Newsletter />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

